import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
} from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import Login from './pages/Login';
import Dashboard from './pages/ADMIN/Dashboard';
import Companies from './pages/ADMIN/Companies';
import CompanyDetails from './pages/ADMIN/CompaniesDetails';
import SystemRoles from './pages/ADMIN/SystemRole';
import SystemRoleDetails from './pages/ADMIN/SystemRoleDetails';
import ManageUser from './pages/ADMIN/ManageUsers';
import ManageUsersDetails from './pages/ADMIN/ManageUsersDetails';
import PrivateRoute from '../PrivateRoute';
import Security from './pages/ADMIN/Security';
import PasswordSettings from './pages/ADMIN/PasswordSettings';
import Interfaces from './pages/ADMIN/Interfaces';
import InterfacesHR from './pages/ADMIN/InterfacesHR';
import InterfacesHRDetails from './pages/ADMIN/InterfacesHRDetails';
import InterfacesApp from './pages/ADMIN/InterfacesApp';
import InterfacesHRHome from './pages/ADMIN/InterfacesHRHome';
import InterfacesAppHome from './pages/ADMIN/InterfaceAppHome';
import ManageApplications from './pages/ADMIN/Applications';
import ManageApplicationsDetails from './pages/ADMIN/ApplicationDetails';
import Cookies from 'js-cookie';
import SysOwnerDashboard from './pages/SYSOWNER/SysOwnerDashboard';
import SysOwnerApplications from './pages/SYSOWNER/MyApplications';
import SysOwnerApplicationsUsers from './pages/SYSOWNER/MyApplicationsUsers';
import MyApplicationGeneralInfo from './pages/SYSOWNER/MyApplicationGeneralInfo';
import MyApplicationManageUsers from './pages/SYSOWNER/MyApplicationManageUser';
import MyApplicationControls from './pages/SYSOWNER/MyApplicationControls';
import SysOwnerCompliance from './pages/SYSOWNER/MyCompliance';
import IAAuditDashboard from './pages/AUDIT/IADashboard';
import AuditRiskLibrary from './pages/AUDIT/RiskLibrary';
import ControlsLibrary from './pages/AUDIT/ControlsLibrary';
import AuditProjects from './pages/AUDIT/Projects';
import AuditProjectSettings from './pages/AUDIT/ProjectSettings';
import RiskandControlMapping from './pages/AUDIT/RiskandControl';
import RiskMapping from './pages/AUDIT/RiskMapping';
import RiskMappingStepper from './pages/AUDIT/RiskMappingStepper';
import Workpapers from './pages/AUDIT/Workpapers';
import WorkpapersDetails from './pages/AUDIT/WorkpaperDetails';
import ProcedureLibrary from './pages/AUDIT/ProcedureLibrary';
import ProcessNarrative from './pages/SYSOWNER/ProcessNarrative';
import ProcessNarrativeDetails from './pages/SYSOWNER/ProcessNarrativeDetails';
import MyAccessRequest from './pages/SYSOWNER/MyAccessRequests';
import RoleMatrix from './pages/SYSOWNER/RoleMatrix';
import UserAccessReview from './pages/SYSOWNER/UserAccessReview';
import UserAccessReviewDetails from './pages/SYSOWNER/UserAccessReviewDetails';
import UserAccessReviewData from './pages/SYSOWNER/UserAccessReviewData';
import Register from './pages/ONBOARDING/Registration';
import LandingPagev2 from './pages/ONBOARDING/LandingPagev2';
import InterfaceDetailsPerApp from './pages/ADMIN/InterfaceAppDetails';
import NetworkLayer from './pages/ADMIN/NetworkSetup';

//v2
import LoginForm from './pages/LoginForm';
import Dashboardv2 from './pages/ADMIN/Dashboardv2';
import ManageApplicationsv2 from './pages/ADMIN/Applicationsv2';
import EmployeeRecord from './pages/ADMIN/EmployeeRecord';
import UARApprovalView from './pages/SYSOWNER/UserAccessReviewApprovalView';
import AuditRequests from './pages/AUDIT/AuditRequests';
import AuditRequestDetails from './pages/AUDIT/AuditRequestDetails';
import ManageAccess from './pages/AUDIT/ManageAccess';
import PasswordReset from './pages/ONBOARDING/PasswordReset';
import ClientDashboard from './pages/CLIENT/RequestDashboard';
import UnauthorizedAccess from './common/UnauthorizedAccess';
import RequestDetails from './pages/CLIENT/RequestDetails';
import PolicyLibrary from './pages/AUDIT/PolicyLibrary';
import SODLibrary from './pages/AUDIT/SODLibrary';    
import ProcessNarrativeLibrary from './pages/AUDIT/ProcessNarrative';
import NotFound from './common/NotFound';
import AccessRequestDashboard from './pages/USER/RequestDashboard';
import RequestForm from './pages/USER/RequestForm';
import RequestSuccess from './pages/USER/RequestSuccess';
import ScrollTest from './pages/ScrollTest';
import BlogPage from './pages/DEFAULT/BlogPage';
import BlogPostDetail from './pages/DEFAULT/BlogPostDetail';
import BlogManager from './pages/ADMIN/BlogManager';
import HRRecordUpload from './pages/ADMIN/HRRecordUpload';
import HRSFTPConfig from './pages/ADMIN/HRSFTPConfig';
import HRJobSchedule from './pages/ADMIN/HRJobSchedule';
import HRDataManagement from './pages/ADMIN/HRDataManagement';
import HRChangeHistory from './pages/ADMIN/HRChangeHistory';
import HRJobStatus from './pages/ADMIN/HRJobStatus';
import HRAudit from './pages/ADMIN/HRAudit';
import PlanningDetails from './pages/AUDIT/PlanningDetails';
import PlanningDocuments from './pages/AUDIT/PlanningDocuments';

// Add the import for ChatSupportProvider
import { ChatSupportProvider } from '../contexts/ChatSupportContext';

const App = () => {
  const token = Cookies.get('token');
  const storedGroups = Cookies.get('groups');
  const auditRole = Cookies.get('audit_role');
  const groups = storedGroups ? JSON.parse(storedGroups) : [];

  // Function to check if a group exists in the user's groups
  const isGroupMember = (requiredGroup) => {
    return groups.includes(requiredGroup);
  };

  // Function to check if user has a specific audit role
  const hasAuditRole = (requiredRole) => {
    return auditRole === requiredRole;
  };

  // Function to determine if user has any group membership
  const hasAnyGroup = () => {
    return groups.length > 0;
  };

  return (
    <ChatSupportProvider>
      <SnackbarProvider maxSnack={3}>
        <Router>
          <Routes>
            {/* Public route */}
            <Route path="/login" element={<LoginForm />} />
            <Route path="/loginform" element={<LoginForm />} />
            <Route path="" element={<LandingPagev2 />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password" element={<PasswordReset />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostDetail />} />
            <Route path="/accessreview/:token" element={<UARApprovalView />} />
            <Route path="/myrequests" element={<AccessRequestDashboard />} />
            <Route path="/request/form" element={<RequestForm />} />
            <Route path="/request/success/:id" element={<RequestSuccess />} />
            <Route path="/scrolltest" element={<ScrollTest />} />

            {/* Internal Audit routes - Role only (no group) */}
            {(!hasAnyGroup() && hasAuditRole('Internal Audit')) && (
              <Route path="/" element={<PrivateRoute requiredRole="Internal Audit" />}>
                <Route index path="/" element={<IAAuditDashboard />} />
                <Route index path="/dashboard" element={<IAAuditDashboard />} />
                <Route path="/audit/risklibrary" element={<AuditRiskLibrary />} />
                <Route path="/audit/controlslibrary" element={<ControlsLibrary />} />
                <Route path="/audit/projects" element={<AuditProjects />} />
                <Route path="/audit/projects/:id" element={<AuditProjectSettings />} />
                <Route path="/audit/planning/:id" element={<PlanningDetails />} />
                <Route path="/audit/projects/:id/planning" element={<PlanningDocuments />} />
                <Route path="/audit/projects/:id/planning/:document_type" element={<PlanningDetails />} />
                <Route path="/audit/riskandcontrolmapping/:id" element={<RiskandControlMapping />} />
                <Route path="/audit/riskandcontrolmapping/application/:company_id/:id" element={<RiskMapping />} />
                <Route path="/audit/riskmappingstepper/application/:company_id/:id" element={<RiskMappingStepper />} />
                <Route path="/audit/workpapers/:id" element={<Workpapers />} />
                <Route path="/audit/workpapers/project/:id/app/:app_id/control/:controlref" element={<WorkpapersDetails />} />
                <Route path="/audit/procedurelibrary" element={<ProcedureLibrary />} />
                <Route path="/audit/auditrequests/:id" element={<AuditRequests />} />
                <Route path="/audit/project/:project_id/requests/:id" element={<AuditRequestDetails />} />
                <Route path="/audit/manageaccess/:id" element={<ManageAccess />} />
                <Route path="/audit/policylibrary" element={<PolicyLibrary />} />
                <Route path="/audit/sodlibrary" element={<SODLibrary />} />
                <Route path="/processnarrative" element={<ProcessNarrative />} />
                <Route path="/processnarrative/:company/:id" element={<ProcessNarrativeDetails />} />
                <Route path="/processnarrative/:id" element={<ProcessNarrativeDetails />} />
                <Route path="/audit/processnarrative" element={<ProcessNarrativeLibrary />} />
                
              </Route>
            )}

            {/* Client Participant routes - Role only (no group) */}
            {(!hasAnyGroup() && hasAuditRole('Client Participant')) && (
              <Route path="/" element={<PrivateRoute requiredRole="Client Participant" />}>
                <Route index path="/" element={<ClientDashboard />} />
                <Route index path="/dashboard" element={<ClientDashboard />} />
                <Route path="/myrequests/details/:id" element={<RequestDetails />} />
              </Route>
            )}

            {/* Client Coordinator routes - Role only (no group) */}
            {(!hasAnyGroup() && hasAuditRole('Client Coordinator')) && (
              <Route path="/" element={<PrivateRoute requiredRole="Client Coordinator" />}>
                <Route index path="/" element={<ClientDashboard />} />
                <Route index path="/dashboard" element={<ClientDashboard />} />
                <Route path="/myrequests/details/:id" element={<RequestDetails />} />
              </Route>
            )}

            {/* Internal Audit routes - Group-based */}
            {isGroupMember('Internal Auditor') && (
              <Route path="/" element={<PrivateRoute requiredGroup="Internal Auditor" />}>
                <Route index path="/" element={<IAAuditDashboard />} />
                <Route index path="/dashboard" element={<IAAuditDashboard />} />
                <Route path="/audit/risklibrary" element={<AuditRiskLibrary />} />
                <Route path="/audit/controlslibrary" element={<ControlsLibrary />} />
                <Route path="/audit/projects" element={<AuditProjects />} />
                <Route path="/audit/projects/:id" element={<AuditProjectSettings />} />
                <Route path="/audit/planning/:id" element={<PlanningDetails />} />
                <Route path="/audit/projects/:id/planning" element={<PlanningDocuments />} />
                <Route path="/audit/projects/:id/planning/:document_type" element={<PlanningDetails />} />
                <Route path="/audit/riskandcontrolmapping/:id" element={<RiskandControlMapping />} />
                <Route path="/audit/riskandcontrolmapping/application/:company_id/:id" element={<RiskMapping />} />
                <Route path="/audit/riskmappingstepper/application/:company_id/:id" element={<RiskMappingStepper />} />
                <Route path="/audit/workpapers/:id" element={<Workpapers />} />
                <Route path="/audit/workpapers/project/:id/app/:app_id/control/:controlref" element={<WorkpapersDetails />} />
                <Route path="/audit/procedurelibrary" element={<ProcedureLibrary />} />
                <Route path="/audit/auditrequests/:id" element={<AuditRequests />} />
                <Route path="/audit/project/:project_id/requests/:id" element={<AuditRequestDetails />} />
                <Route path="/audit/manageaccess/:id" element={<ManageAccess />} />
                <Route path="/audit/policylibrary" element={<PolicyLibrary />} />
                <Route path="/audit/sodlibrary" element={<SODLibrary />} />
                <Route path="/processnarrative" element={<ProcessNarrative />} />
                <Route path="/processnarrative/:company/:id" element={<ProcessNarrativeDetails />} />
                <Route path="/processnarrative/:id" element={<ProcessNarrativeDetails />} />
                <Route path="/audit/processnarrative" element={<ProcessNarrativeLibrary />} />
               
              </Route>
            )}

            {/* Administrator routes - Role only (no group) */}
            {(!hasAnyGroup() && hasAuditRole('Administrator')) && (
              <Route path="/" element={<PrivateRoute requiredRole="Administrator" />}>
                <Route index path="/" element={<Dashboardv2 />} />
                <Route index path="/dashboard" element={<Dashboardv2 />} />
                <Route path="/companies" element={<Companies />} />
                <Route path="/companies/:companyId" element={<CompanyDetails />} />
                <Route path="/systemroles" element={<SystemRoles />} />
                <Route path="/systemroles/:id" element={<SystemRoleDetails />} />
                <Route path="/manageusers" element={<ManageUser />} />
                <Route path="/manageusers/:username" element={<ManageUsersDetails />} />
                <Route path="/security" element={<Security />} />
                <Route path="/networklayer" element={<NetworkLayer />} />
                <Route path="/interfaces" element={<Interfaces />} />
                <Route path="/interfaces/hr" element={<InterfacesHR />} />
                <Route path="/interfaces/hr/home" element={<InterfacesHRHome />} />
                <Route path="/interfaces/hr/details" element={<InterfacesHRDetails />} />
                <Route path="/interfaces/hr/sftp" element={<HRSFTPConfig />} />
                <Route path="/interfaces/hr/jobs" element={<HRJobSchedule />} />
                <Route path="/interfaces/hr/upload" element={<HRRecordUpload />} />
                <Route path="/interfaces/hr/changes" element={<HRChangeHistory />} />
                <Route path="/interfaces/hr/job-status" element={<HRJobStatus />} />
                <Route path="/interfaces/hr/audit" element={<HRAudit />} />
                <Route path="/hr-data-management" element={<HRDataManagement />} />
                {/* Case-insensitive alternate HR paths */}
                <Route path="/Interfaces/HR/Home" element={<HRDataManagement />} />
                <Route path="/Interfaces/HR/Changes" element={<HRChangeHistory />} />
                <Route path="/Interfaces/HR/SFTP" element={<HRSFTPConfig />} />
                <Route path="/Interfaces/HR/Upload" element={<HRRecordUpload />} />
                <Route path="/Interfaces/HR/Jobs" element={<HRJobSchedule />} />
                <Route path="/Interfaces/HR/Job-Status" element={<HRJobStatus />} />
                <Route path="/Interfaces/HR/Audit" element={<HRAudit />} />
                <Route path="/interfaces/applications/:id" element={<InterfaceDetailsPerApp />} />
                <Route path="/interfaces/applications" element={<InterfacesApp />} />
                <Route path="/interfaces/applications/home" element={<ManageApplicationsv2 />} />
                <Route path="/security/passwordconfiguration" element={<PasswordSettings />} />
                <Route path="/applications" element={<ManageApplicationsv2 />} />
                <Route path="/hrrecord" element={<EmployeeRecord />} />
                <Route path="/applications/:id" element={<ManageApplicationsDetails />} />
                <Route path="/applications/manageusers/:id" element={<MyApplicationManageUsers />} />
                <Route path="/audit/risklibrary" element={<AuditRiskLibrary />} />
                <Route path="/audit/controlslibrary" element={<ControlsLibrary />} />
                <Route path="/audit/projects" element={<AuditProjects />} />
                <Route path="/audit/projects/:id" element={<AuditProjectSettings />} />
                <Route path="/audit/planning/:id" element={<PlanningDetails />} />
                <Route path="/audit/projects/:id/planning" element={<PlanningDocuments />} />
                <Route path="/audit/projects/:id/planning/:document_type" element={<PlanningDetails />} />
                <Route path="/audit/riskandcontrolmapping/:id" element={<RiskandControlMapping />} />
                <Route path="/audit/riskandcontrolmapping/application/:company_id/:id" element={<RiskMapping />} />
                <Route path="/audit/riskmappingstepper/application/:company_id/:id" element={<RiskMappingStepper />} />
                <Route path="/audit/workpapers/:id" element={<Workpapers />} />
                <Route path="/audit/workpapers/project/:id/app/:app_id/control/:controlref" element={<WorkpapersDetails />} />
                <Route path="/audit/procedurelibrary" element={<ProcedureLibrary />} />
                <Route path="/audit/auditrequests/:id" element={<AuditRequests />} />
                <Route path="/audit/project/:project_id/requests/:id" element={<AuditRequestDetails />} />
                <Route path="/audit/manageaccess/:id" element={<ManageAccess />} />
                <Route path="/blogmanager" element={<BlogManager />} />
               
              </Route>
            )}

            {/* Administrator routes - Group-based */}
            {isGroupMember('Administrator') && (
              <Route path="/" element={<PrivateRoute requiredGroup="Administrator" />}>
                <Route index path="/" element={<Dashboardv2 />} />
                <Route index path="/dashboard" element={<Dashboardv2 />} />
                <Route path="/companies" element={<Companies />} />
                <Route path="/companies/:companyId" element={<CompanyDetails />} />
                <Route path="/systemroles" element={<SystemRoles />} />
                <Route path="/systemroles/:id" element={<SystemRoleDetails />} />
                <Route path="/manageusers" element={<ManageUser />} />
                <Route path="/manageusers/:username" element={<ManageUsersDetails />} />
                <Route path="/security" element={<Security />} />
                <Route path="/networklayer" element={<NetworkLayer />} />
                <Route path="/interfaces" element={<Interfaces />} />
                <Route path="/interfaces/hr" element={<InterfacesHR />} />
                <Route path="/interfaces/hr/home" element={<InterfacesHRHome />} />
                <Route path="/interfaces/hr/details" element={<InterfacesHRDetails />} />
                <Route path="/interfaces/hr/sftp" element={<HRSFTPConfig />} />
                <Route path="/interfaces/hr/jobs" element={<HRJobSchedule />} />
                <Route path="/interfaces/hr/upload" element={<HRRecordUpload />} />
                <Route path="/interfaces/hr/changes" element={<HRChangeHistory />} />
                <Route path="/interfaces/hr/job-status" element={<HRJobStatus />} />
                <Route path="/interfaces/hr/audit" element={<HRAudit />} />
                <Route path="/hr-data-management" element={<HRDataManagement />} />
                {/* Case-insensitive alternate HR paths */}
                <Route path="/Interfaces/HR/Home" element={<HRDataManagement />} />
                <Route path="/Interfaces/HR/Changes" element={<HRChangeHistory />} />
                <Route path="/Interfaces/HR/SFTP" element={<HRSFTPConfig />} />
                <Route path="/Interfaces/HR/Upload" element={<HRRecordUpload />} />
                <Route path="/Interfaces/HR/Jobs" element={<HRJobSchedule />} />
                <Route path="/Interfaces/HR/Job-Status" element={<HRJobStatus />} />
                <Route path="/Interfaces/HR/Audit" element={<HRAudit />} />
                <Route path="/interfaces/applications/:id" element={<InterfaceDetailsPerApp />} />
                <Route path="/interfaces/applications" element={<InterfacesApp />} />
                <Route path="/interfaces/applications/home" element={<ManageApplicationsv2 />} />
                <Route path="/security/passwordconfiguration" element={<PasswordSettings />} />
                <Route path="/applications" element={<ManageApplicationsv2 />} />
                <Route path="/hrrecord" element={<EmployeeRecord />} />
                <Route path="/applications/:id" element={<ManageApplicationsDetails />} />
                <Route path="/applications/manageusers/:id" element={<MyApplicationManageUsers />} />
                <Route path="/audit/risklibrary" element={<AuditRiskLibrary />} />
                <Route path="/audit/controlslibrary" element={<ControlsLibrary />} />
                <Route path="/audit/projects" element={<AuditProjects />} />
                <Route path="/audit/projects/:id" element={<AuditProjectSettings />} />
                <Route path="/audit/planning/:id" element={<PlanningDetails />} />
                <Route path="/audit/projects/:id/planning" element={<PlanningDocuments />} />
                <Route path="/audit/projects/:id/planning/:document_type" element={<PlanningDetails />} />
                <Route path="/audit/riskandcontrolmapping/:id" element={<RiskandControlMapping />} />
                <Route path="/audit/riskandcontrolmapping/application/:company_id/:id" element={<RiskMapping />} />
                <Route path="/audit/riskmappingstepper/application/:company_id/:id" element={<RiskMappingStepper />} />
                <Route path="/audit/workpapers/:id" element={<Workpapers />} />
                <Route path="/audit/workpapers/project/:id/app/:app_id/control/:controlref" element={<WorkpapersDetails />} />
                <Route path="/audit/procedurelibrary" element={<ProcedureLibrary />} />
                <Route path="/audit/auditrequests/:id" element={<AuditRequests />} />
                <Route path="/audit/project/:project_id/requests/:id" element={<AuditRequestDetails />} />
                <Route path="/audit/manageaccess/:id" element={<ManageAccess />} />
                <Route path="/blogmanager" element={<BlogManager />} />
               
              </Route>
            )}

            {/* Process Owner routes - Role only (no group) */}
            {(!hasAnyGroup() && hasAuditRole('Process Owner')) && (
              <Route path="/" element={<PrivateRoute requiredRole="Process Owner" />}>
                <Route index path="/" element={<SysOwnerDashboard />} />
                <Route index path="/dashboard" element={<SysOwnerDashboard />} />
                <Route path="/applications" element={<SysOwnerApplications />} />
                <Route path="/applications/users/:id" element={<SysOwnerApplicationsUsers />} />
                <Route path="/applications/setupinfo/:id" element={<MyApplicationGeneralInfo />} />
                <Route path="/applications/manageusers/:id" element={<MyApplicationManageUsers />} />
                <Route path="/applications/controls/:id" element={<MyApplicationControls />} />
                <Route path="/applications/rolematrix/:id" element={<RoleMatrix />} />
                <Route path="/useraccessreview" element={<UserAccessReview />} />
                <Route path="/useraccessreview/:id" element={<UserAccessReviewDetails />} />
                <Route path="/useraccessreview/data/:id/:phase" element={<UserAccessReviewData />} />
                <Route path="/compliance" element={<SysOwnerCompliance />} />
                <Route path="/processnarrative" element={<ProcessNarrative />} />
                <Route path="/processnarrative/:company/:id" element={<ProcessNarrativeDetails />} />
                <Route path="/processnarrative/:id" element={<ProcessNarrativeDetails />} />
               
              
              </Route>
            )}

            {/* Process Owner routes - Group-based */}
            {isGroupMember('Process Owner') && (
              <Route path="/" element={<PrivateRoute requiredGroup="Process Owner" />}>
                <Route index path="/" element={<SysOwnerDashboard />} />
                <Route index path="/dashboard" element={<SysOwnerDashboard />} />
                <Route path="/applications" element={<SysOwnerApplications />} />
                <Route path="/applications/users/:id" element={<SysOwnerApplicationsUsers />} />
                <Route path="/applications/setupinfo/:id" element={<MyApplicationGeneralInfo />} />
                <Route path="/applications/manageusers/:id" element={<MyApplicationManageUsers />} />
                <Route path="/applications/controls/:id" element={<MyApplicationControls />} />
                <Route path="/applications/rolematrix/:id" element={<RoleMatrix />} />
                <Route path="/useraccessreview" element={<UserAccessReview />} />
                <Route path="/useraccessreview/:id" element={<UserAccessReviewDetails />} />
                <Route path="/useraccessreview/data/:id/:phase" element={<UserAccessReviewData />} />
                <Route path="/compliance" element={<SysOwnerCompliance />} />
                <Route path="/processnarrative" element={<ProcessNarrative />} />
                <Route path="/processnarrative/:company/:id" element={<ProcessNarrativeDetails />} />
                <Route path="/processnarrative/:id" element={<ProcessNarrativeDetails />} />
               
                <Route path="/accessrequest/granting" element={<MyAccessRequest />} />
               
              </Route>
            )}

            {/* Unauthorized access route */}
            <Route path="/unauthorized" element={<UnauthorizedAccess />} />

            {/* 404 Not Found - this must be the last route! */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
      </SnackbarProvider>
    </ChatSupportProvider>
  );
};

export default App;
